import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  useTheme,
  Button,
} from '@mui/material'
import { useDialogStore, DialogType } from '@/store/dialogStore'
import {
  IconAlertTriangle,
  IconAlertCircle,
  IconCircleCheck,
  IconInfoCircle,
  IconHelpCircle,
} from '@tabler/icons-react'
import TruncatedText from '../Text/TruncatedText'

/**
 * Maps dialog types to icon components and colors
 */
const dialogTypeConfig: Record<
  DialogType,
  {
    icon: React.ElementType
    color: string
  }
> = {
  confirmation: { icon: IconHelpCircle, color: 'primary.main' },
  info: { icon: IconInfoCircle, color: 'info.main' },
  warning: { icon: IconAlertTriangle, color: 'warning.main' },
  error: { icon: IconAlertCircle, color: 'error.main' },
  success: { icon: IconCircleCheck, color: 'success.main' },
}

/**
 * Global Dialog Component
 *
 * A site-wide dialog component that can be used for confirmations, warnings,
 * info messages, etc. from anywhere in the application.
 *
 * Usage:
 * - Import useDialogStore in any component
 * - Call openDialog() with title, message, actions, and optional parameters
 */
export const GlobalDialog = () => {
  const { open, title, message, actions, size, type, closeDialog } = useDialogStore()
  const theme = useTheme()

  const { icon: Icon, color } = dialogTypeConfig[type]

  return (
    <Dialog
      open={open}
      onClose={() => closeDialog()}
      maxWidth={size}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box sx={{ color }}>
          <Icon size={24} stroke={1.5} />
        </Box>
        <Box
          sx={{
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.6,
            fontFamily: theme.typography.fontFamily,
          }}
        >
          {title}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 1 }}>
        {typeof message === 'string' ? (
          <TruncatedText
            text={message}
            lines={3}
            sx={{
              color: 'text.primary',
              mb: actions.length > 0 ? 2 : 0,
              fontSize: '0.875rem',
              lineHeight: 1.5,
              width: '100%',
              display: 'block',
            }}
          />
        ) : (
          <DialogContentText
            sx={{
              color: 'text.primary',
              mb: actions.length > 0 ? 2 : 0,
              fontSize: '0.875rem',
              lineHeight: 1.5,
              width: '100%',
              display: 'block',
            }}
          >
            {message}
          </DialogContentText>
        )}
      </DialogContent>

      {actions.length > 0 && (
        <DialogActions
          sx={{
            pb: 2.5,
            px: 2.5,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          {actions.map((action, index) => (
            <Button
              key={index}
              onClick={() => {
                action.onClick()
                // Don't auto-close if the action has its own closeDialog call
                if (!action.variant || action.variant !== 'contained') {
                  closeDialog()
                }
              }}
              variant={action.variant || 'text'}
              color={action.color || 'inherit'}
              autoFocus={action.autoFocus}
              sx={{
                borderRadius: '4px',
                minWidth: '88px',
                height: '36px',
                textTransform: 'none',
                fontWeight: 500,
                py: 0.75,
                px: 2.5,
                ...(action.variant === 'outlined' && {
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.action.hover,
                  '&:hover': {
                    borderColor: theme.palette.divider,
                    backgroundColor: theme.palette.action.selected,
                  },
                }),
                ...(action.variant === 'contained' &&
                  action.color === 'error' && {
                    backgroundColor: theme.palette.error.main,
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: theme.palette.error.dark,
                    },
                  }),
              }}
            >
              {action.label}
            </Button>
          ))}
        </DialogActions>
      )}
    </Dialog>
  )
}

export default GlobalDialog
